import { div } from './format.js';

/* --------------------------------------------------------------------------
   Veckodag × klockslag: när är det tryck i kassan?

   Värmekartan ska svara på en bemanningsfråga, och då är det antalet köp som
   räknas, inte omsättningen: ett köp på 2 000 kr tar inte längre tid i
   kassan än ett på 50 kr. Omsättning finns som andra mått.

   Värdet i varje ruta är ett snitt per dag, inte en summa över perioden. En
   månad har fyra eller fem av varje veckodag; med summor ser en veckodag med
   fem tillfällen ut att ha mer tryck än den har. Snittet räknas på de dagar
   den veckodagen hade försäljning i urvalet — samma sätt som "Omsättning per
   dag" räknas — så att en stängd helgdag inte drar ner snittet.

   Snittet döljer toppar, och det är topparna som ger kö. Därför bär varje
   ruta också det högsta antalet köp en enskild dag (maxTx).

   Timmarna är timmarna med köp, och glapp på högst MAX_GAP timmar fylls i så
   att en lunchstängning syns. Längre glapp hoppas över: ett enstaka kvitto
   strax efter midnatt ska inte ge sex tomma kolumner. Samma timmar som i
   diagrammen per klockslag (activeHours()).
   -------------------------------------------------------------------------- */

/** Längsta glapp av timmar utan försäljning som fylls i. */
export const MAX_GAP = 2;

/** Timmarna med köp i stigande ordning, med korta glapp ifyllda. */
export function hourSpan(hoursWithSales){
  const hrs = [...new Set(hoursWithSales)].filter(h => Number.isInteger(h)).sort((a, b) => a - b);
  const out = [];
  for(const h of hrs){
    const prev = out[out.length - 1];
    if(prev !== undefined && h - prev - 1 <= MAX_GAP){
      for(let x = prev + 1; x < h; x++) out.push(x);
    }
    out.push(h);
  }
  return out;
}

/**
 * receipts: kvitton med weekday (måndag 0) och hour, day, revenue.
 * Ger {days, hours, cell(d, h), dayCount, max, peak, busiest} där cell ger
 * {tx, revenue, txPerDay, revenuePerDay, maxTx}, eller null utan kvitton.
 * busiest är de tre rutorna med flest köp per dag, peak den första av dem.
 */
export function heatGrid(receipts){
  if(!receipts.length) return null;
  const cells = new Map();
  const datesPerDay = new Map();     // veckodag -> Set med datum
  const perDate = new Map();         // ruta + datum -> antal köp
  const seen = new Set();
  for(const r of receipts){
    const k = r.weekday * 24 + r.hour;
    let c = cells.get(k);
    if(!c){ c = {tx:0, revenue:0, maxTx:0}; cells.set(k, c); }
    c.tx += 1; c.revenue += r.revenue;
    const dk = k + '|' + r.day;
    const n = (perDate.get(dk) || 0) + 1;
    perDate.set(dk, n);
    if(n > c.maxTx) c.maxTx = n;
    let s = datesPerDay.get(r.weekday);
    if(!s){ s = new Set(); datesPerDay.set(r.weekday, s); }
    s.add(r.day);
    seen.add(r.hour);
  }
  const days = [...datesPerDay.keys()].sort((a, b) => a - b);
  const hours = hourSpan(seen);
  const dayCount = new Map([...datesPerDay].map(([d, s]) => [d, s.size]));

  const cell = (d, h) => {
    const c = cells.get(d * 24 + h) || {tx:0, revenue:0, maxTx:0};
    const n = dayCount.get(d) || 0;
    return {tx: c.tx, revenue: c.revenue, maxTx: c.maxTx,
            txPerDay: div(c.tx, n), revenuePerDay: div(c.revenue, n)};
  };

  // Rutorna med flest köp per dag; vid lika vinner den tidigaste
  const all = [];
  const max = {txPerDay: 0, revenuePerDay: 0};
  for(const d of days) for(const h of hours){
    const c = cell(d, h);
    if(c.tx > 0) all.push({day: d, hour: h, ...c});
    if(c.txPerDay > max.txPerDay) max.txPerDay = c.txPerDay;
    if(c.revenuePerDay > max.revenuePerDay) max.revenuePerDay = c.revenuePerDay;
  }
  const busiest = all.sort((a, b) => b.txPerDay - a.txPerDay || a.day - b.day || a.hour - b.hour).slice(0, 3);
  return {days, hours, cell, dayCount, max, peak: busiest[0] || null, busiest};
}
